import { HowToRegOutlined, Person as PersonIcon, WorkHistoryOutlined, Work as WorkIcon } from "@mui/icons-material";
import { Box, Button, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SyntheticEvent, useEffect, useState } from "react";
import { useModals } from "../../../context/ModalContext";
import { useGlobalSnackbar } from "../../../context/SnackbarContext";
// @ts-ignore
import { getWorkforceByUid, patchStaffType } from "../../../utils";
import { colors, alphaVar } from "../../ui/colors";
// @ts-ignore
import { WorkforcePersonalInfo } from "../WorkforceInfoView/view/WorkforcePersonalInfo";
import { useUpdateWorkforce } from "../WorkforceInfoView/components/hooks/useWorkForce";
import { WorkforceDetailTabs } from "./WorkforceDetailTabs";
// @ts-ignore
import { WorkforceTasks } from "./WorkforceTasks";
// @ts-ignore
import { WorkforceJobsDetails } from "./WorkforceJobsDetails";

const TAB_DEFINITIONS = [
  { value: 0, icon: <PersonIcon />, label: "General" },
  { value: 1, icon: <WorkIcon />, label: "Jobs Visits" },
  { value: 2, icon: <WorkHistoryOutlined />, label: "Work Records" },
];

const TAB_SX = (isSelected: boolean) => ({
  textTransform: "none",
  fontWeight: isSelected ? 600 : 500,
  fontSize: "0.85rem",
  minHeight: 48,
  transition: "all 0.2s ease",
  color: isSelected ? colors.orange.base : colors.gray[400],
  opacity: isSelected ? 1 : 0.8,
  "&:hover": {
    opacity: 1,
    color: colors.gray[600],
  },
  "&.Mui-selected": {
    color: colors.orange.base,
  },
  "& .MuiSvgIcon-root": {
    fontSize: 18,
    marginRight: 1,
  },
});

const TABS_SX = {
  mb: 2,
  minHeight: 48,
  borderBottom: `1px solid ${colors.gray[100]}`,
  "& .MuiTabs-indicator": {
    backgroundColor: colors.orange.base,
    height: 2.5,
    borderRadius: "2px 2px 0 0",
  },
};

interface WorkforceDetailProps {
  workforce?: any;
  workforceUid?: string;
  onRoleChanged?: (role: string) => void;
}

export const WorkforceDetail = ({ workforce: initialWorkforce, workforceUid: propUid, onRoleChanged }: WorkforceDetailProps) => {
  const uid = propUid || initialWorkforce?.uid;

  const { data: fetchedWorkforce, isLoading } = useQuery<any>({
    queryKey: ["workforce", uid],
    queryFn: () => getWorkforceByUid(sessionStorage.getItem("token"), uid!),
    enabled: !!uid,
    initialData: initialWorkforce,
  });

  console.log("workforce data: ", initialWorkforce);

  const workforce = fetchedWorkforce || initialWorkforce || null;

  const [selectedRole, setSelectedRole] = useState(workforce?.workforceRole || workforce?.type || "");
  const [detailTab, setDetailTab] = useState(0);
  const queryClient = useQueryClient();
  const { showDialog } = useModals();
  const { showSnackbar } = useGlobalSnackbar();
  const updateWorkforceMutation = useUpdateWorkforce();

  useEffect(() => {
    if (workforce?.uid) {
      setSelectedRole(workforce?.workforceRole || workforce?.type || "");
    }
  }, [workforce]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!workforce) return null;

  const isApplicant = workforce.status === "APPLICANT";
  const availableTabs = isApplicant ? TAB_DEFINITIONS.filter((t) => t.value === 0) : TAB_DEFINITIONS;

  const handleDetailTabChange = (_event: SyntheticEvent, newValue: number) => {
    if (newValue !== 3) setDetailTab(newValue);
  };

  const handleRoleChange = async (staffType: string) => {
    const token = sessionStorage.getItem("token");
    try {
      await patchStaffType({ token, uid: workforce?.uid, staffType });

      // Optimistically update the list so the item doesn't disappear when switching tabs
      queryClient.setQueryData<any[]>(["workforces"], (oldData) => {
        if (!oldData) return oldData;
        return oldData.map((wf) => (wf.uid === workforce?.uid ? { ...wf, workforceRole: staffType, type: staffType, role: staffType } : wf));
      });

      setSelectedRole(staffType);
      if (onRoleChanged) onRoleChanged(staffType);

      queryClient.invalidateQueries({ queryKey: ["workforces"] });
      queryClient.invalidateQueries({ queryKey: ["total-workforce"] });
    } catch (error) {
      console.error(error);
    }
  };

  const handleConvertApplicant = () => {
    const memberName = [workforce.firstName, workforce.lastName].filter(Boolean).join(" ") || "this applicant";
    showDialog({
      type: "confirm",
      title: "Convert to Workforce Member?",
      message: `Are you sure you want to convert ${memberName} to an active workforce member?`,
      confirmLabel: "Yes, Convert",
      cancelLabel: "Cancel",
      onConfirm: () => {
        updateWorkforceMutation.mutate(
          { uid: workforce.uid, data: { status: "ACTIVE" } },
          {
            onSuccess: () => {
              showSnackbar(`${memberName} has been converted to an active workforce member.`, "success");
            },
            onError: (err: any) => {
              showSnackbar(err?.response?.data?.message || err?.message || "Failed to update status", "error");
            },
          },
        );
      },
    });
  };

  return (
    <Box sx={{ p: 1 }}>
      {isApplicant && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: "12px",
            bgcolor: alphaVar(colors.orange.base, 0.08),
            border: `1px solid ${alphaVar(colors.orange.base, 0.2)}`,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.orange.base }}>
              Job Applicant
            </Typography>
            <Typography variant="caption" sx={{ color: colors.gray[600] }}>
              This contact is currently registered as an applicant.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<HowToRegOutlined sx={{ fontSize: 18 }} />}
            onClick={handleConvertApplicant}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.8125rem",
              px: 2.5,
              py: 0.8,
              borderRadius: "20px",
              background: `linear-gradient(135deg, ${colors.orange.base} 0%, #E67E00 100%)`,
              color: "#ffffff",
              boxShadow: `0 2px 8px ${alphaVar(colors.orange.base, 0.25)}`,
              whiteSpace: "nowrap",
              "&:hover": {
                background: `linear-gradient(135deg, #E67E00 0%, #CC7000 100%)`,
                boxShadow: `0 4px 12px ${alphaVar(colors.orange.base, 0.35)}`,
              },
            }}
          >
            Convert to Workforce Member
          </Button>
        </Box>
      )}

      {availableTabs.length > 1 && (
        <Tabs value={detailTab} onChange={handleDetailTabChange} variant="fullWidth" sx={TABS_SX}>
          {availableTabs.map(({ value, icon, label }) => (
            <Tab key={value} value={value} icon={icon} label={label} sx={TAB_SX(detailTab === value)} />
          ))}
        </Tabs>
      )}

      {detailTab === 0 && <WorkforcePersonalInfo workforce={workforce} selectedRole={selectedRole} onRoleChange={handleRoleChange} />}
      {!isApplicant && detailTab === 1 && <WorkforceTasks workforce={workforce} />}
      {!isApplicant && detailTab === 2 && <WorkforceJobsDetails workforce={workforce} />}
    </Box>
  );
};
